/* eslint-disable @typescript-eslint/no-explicit-any */
import { Combobox, Field, Option, makeStyles, tokens } from '@fluentui/react-components';
import { useAutomationRuleTrigger } from '../use-trigger';
import { TriggerItemType, WorkItemIsAssignedToErrorType, WorkItemIsAssignedToTriggerType } from '../types';
import { useCallback } from 'react';
import useAssignedToForm from './use-assigned-to-form';
import { useAzureProvider } from 'src/azure/context';

let debounce: any = null;

export default function AssignedToForm(props: {
	data: WorkItemIsAssignedToTriggerType;
	validations: WorkItemIsAssignedToErrorType
}) {
	const classes = useStyles();
	const {setTriggerData, resetValidations} = useAutomationRuleTrigger();
	const {applicationContext} = useAzureProvider();
	const {searchIdentityOptions, getIdentityListOptions} = useAssignedToForm();

	const changeSelectedOption = useCallback((value: string) => {
		resetValidations();
		setTriggerData(oldData => {
			const data = oldData as TriggerItemType;
			const assignedToValues = data.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType;

			return {
				...data,
				workItemIsAssignedTo: {
					...assignedToValues,
					selectedOption: value,
					identity: assignedToValues.identityOptions.find(item => item.value === value)?.label || '',
				}
			};
		});
	}, []);

	const changeTextvalue = useCallback(async (value: string | null) => {
		clearTimeout(debounce);
		if (value === null) {
			const identityOptions = await getIdentityListOptions();

			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				return {
					...data,
					workItemIsAssignedTo: {
						...data.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType,
						identityOptions: identityOptions
					}
				};
			});
		}

		if (value || value === '') {
			setTriggerData(oldData => {
				const data = oldData as TriggerItemType;
				const assignedToValues = data.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType;
	
				return {
					...data,
					workItemIsAssignedTo: {
						...assignedToValues ,
						identity: value as string
					}
				};
			});

			debounce = setTimeout(() => {
				const asyncFn = async () => {
					let options: Array<{label: string; value: string}> = [];
					if (value) {
						options = await searchIdentityOptions(value);
					}

					if (!value) {
						options = await getIdentityListOptions();
					}

					setTriggerData(oldData => {
						const data = oldData as TriggerItemType;
						const assignedToValues = data.workItemIsAssignedTo as WorkItemIsAssignedToTriggerType;
			
						return {
							...data,
							workItemIsAssignedTo: {
								...assignedToValues,
								identityOptions: options,
							}
						};
					});
				};

				asyncFn();
			}, 750);
		}
	}, [applicationContext]);

	return (
		<div className={classes.root}>
			<Field
				className={classes.fieldRoot}
				validationMessage={props.validations.identity}
			>
				<Combobox
					className={classes.dropdown}
					appearance='outline'
					placeholder='Assigned To'
					positioning='below-start'
					value={props.data.identity}
					onOptionSelect={(_, data) => {
						changeSelectedOption(data.optionValue as string);
					}}
					onChange={(ev) => {
						changeTextvalue(ev.target.value);
					}}
				
				>
					{!props.data.identityOptions.length && (
						<Option value="no-options" disabled>
						No options	
						</Option>
					)}
					{props.data.identityOptions.map((item) => (
						<Option key={item.value} value={item.value}>
							{item.label}
						</Option>
					))}
				</Combobox>
			</Field>
		</div>
	);
}

export const useStyles = makeStyles({
	root: {
		width: '100%',
		display: 'flex',
	},
	fieldRoot: {
		width: '100%'
	},
	dropdown: {
		flex: '1 1 0px',
		minWidth: '120px',
		border: `1px solid ${tokens.colorNeutralStroke1}`,
		whiteSpace: 'nowrap',
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		'::after': {
			borderBottom: 'none'
		},
		':hover': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		},
		':active': {
			background: tokens.colorNeutralBackground4Hover,
			border: `1px solid ${tokens.colorNeutralStroke1Hover}`
		}
	}
});